<script setup lang="ts">
import { ref, computed } from 'vue';
import { v5 as uuidv5 } from 'uuid';
import { GradientPoint, pSBC } from '@/lib/marker_colors';

type IndexedGradientPoint = GradientPoint & { id: string }

const props = defineProps<{
  colors: Array<GradientPoint>,
  width: number,
  height: number,
  horizontal?: boolean
}>();

const colors = computed((): Array<IndexedGradientPoint> => {
  return props.colors.map((color) => ({ ...{ offset: '0%', opacity: 1, id: self.crypto.randomUUID() }, ...color }));
});

const marker_path =
  // versione in alto a dx
  "M 238.9336 0 c -131.96 0 -238.9336 106.9912 -238.9336 238.9336 C 0 370.8936 238.9336 783.7062 238.9336 783.7062 S 477.8672 370.8928 477.8672 238.9336 c 0 -131.9424 -106.9744 -238.9336 -238.9336 -238.9336 z m 0 334.4 a 63.4664 63.4664 90 1 1 0 -182.9328 a 63.4664 63.4664 90 0 1 0 182.9328 z";

const x2 = ref<string>(!!props.horizontal ? '100%' : '0%');
const y2 = ref<string>(!props.horizontal ? '100%' : '0%');

const uuid_namespace = 'fff43bc6-9fb2-4db4-856d-2af5a56a4c5c';

const make_id = computed(() => {
  const hor = props.horizontal;

  function encode({ offset, color, opacity }: GradientPoint) {
    return color + `${offset}` + `${opacity}`;
  };
  const hashes = colors.value.map(encode);

  let res = `${hor}`;
  for (const hash of hashes) {
    res += '-' + hash;
  }
  return uuidv5(res, uuid_namespace);
});
const fill_id = computed(() => {
  return 'fill-' + make_id.value;
});
const stroke_id = computed(() => {
  return 'stroke-' + make_id.value;
});
</script>

<template>
  <!-- 783.7062 -->
  <svg viewBox="0 0 477.86 833.7062" :width="width" :height="height" class="icon" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient :id="fill_id" x1="0%" y1="0%" :x2="x2" :y2="y2">
        <!-- <stop offset="0%" style="stop-color: #FF3d00; stop-opacity: 0.6" /> -->
        <!-- <stop offset="100%" style="stop-color: #FF3000; stop-opacity: 1" /> -->
        <stop
          v-for="gradient of colors"
          :key="gradient.id"
          :offset="gradient.offset"
          :style="`stop-color: ${gradient.color}; stop-opacity: ${gradient.opacity}`"
        />
      </linearGradient>
      <linearGradient :id="stroke_id" x1="0%" y1="0%" :x2="x2" :y2="y2">
        <!-- <stop offset="0%" style="stop-color: #FF3dFF; stop-opacity: 1" /> -->
        <!-- <stop offset="100%" style="stop-color: #FF3000; stop-opacity: 1" /> -->
        <stop
          v-for="gradient of colors"
          :key="gradient.id"
          :offset="gradient.offset"
          :style="`stop-color: ${pSBC(-0.6, gradient.color, undefined, undefined)}; stop-opacity: ${gradient.opacity}`"
        />
      </linearGradient>
      <filter id="icon-shadow">
        <feDropShadow dx="5" dy="5" stdDeviation="3" flood-opacity="0.5"/>
      </filter>
      <filter id="featheredEllipse" x="-50%" y="-50%" width="200%" height="200%">
        <feFlood result="floodFill" flood-color="black"/>
        <feComposite in="floodFill" in2="SourceAlpha" operator="in"/>
        <feGaussianBlur in="SourceGraphic" stdDeviation="10"/>
      </filter>
    </defs>
    <!-- d="M 512 85.333 c -164.95 0 -298.667 133.739 -298.667 298.667 C 213.333 548.95 512 1064.9657 512 1064.9657 S 810.667 548.949 810.667 384 c 0 -164.928 -133.718 -298.667 -298.667 -298.667 z m 0 448 a 149.333 149.333 0 1 1 0 -298.666 a 149.333 149.333 0 0 1 0 298.666 z" -->

    <ellipse
      :cx="477.86 / 2" :cy="783.7062" rx="170" ry="40"
      fill="black" fill-opacity="0.2" filter="url(#featheredEllipse)"
    />
    <path
      :d="marker_path"
      :fill="`url(#${fill_id})`"
      :stroke="`url(#${stroke_id})`"
      stroke-width="10"
      transform="translate(5 5) scale(0.979)"
      filter="url(#icon-shadow)"
    />
    <!-- :stroke="pSBC(-0.4, color as string) as string" -->
    <!-- d="M512 85.333c-164.95 0-298.667 133.739-298.667 298.667C213.333 548.95 512 938.667 512 938.667S810.667 548.949 810.667 384c0-164.928-133.718-298.667-298.667-298.667zm0 448a149.333 149.333 0 1 1 0-298.666 149.333 149.333 0 0 1 0 298.666z" -->
  </svg>

  <!-- EDIT HERE: https://editsvgcode.com/ -->
</template>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
